import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';
import { cn } from './utils';

export type InputSize = 'sm' | 'md';
export type InputVariant = 'default' | 'ghost';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  /**
   * Visual variant.
   * - `default`: bordered, padded input — the normal form field.
   * - `ghost`: strips border, background, and padding so the input blends
   *   into its container. Use when the input is embedded in a styled
   *   wrapper (table cell, inline header) that already owns the chrome.
   *   Consumer is responsible for text size/color via className or parent.
   */
  variant?: InputVariant;
  /**
   * - `false` (default): no error state.
   * - `true`: apply error styling only (red border + focus ring).
   * - `string`: apply error styling and render the message under the
   *   input with `role="alert"` and aria-describedby wired.
   */
  error?: boolean | string;
  label?: string;
  helpText?: string;
  /**
   * Classes for the outer wrapper element. Use this for layout that has to
   * apply to the field's box in its parent — e.g. `flex-1 min-w-0` when the
   * field is a flex child. `className` lands on the `<input>`, which is not
   * the flex item, so layout classes there have no effect.
   */
  wrapperClassName?: string;
  /** Decoration rendered inside the input on the left (e.g. an Icon). Pointer-events are disabled. */
  leftSlot?: ReactNode;
  /** Interactive node rendered inside the input on the right (e.g. a password-reveal button). */
  rightSlot?: ReactNode;
  /** Trailing content rendered inline with the label (e.g. a compact picker). */
  labelEnd?: ReactNode;
}

const sizeStyles: Record<InputVariant, Record<InputSize, string>> = {
  default: {
    md: 'px-4 py-3 rounded-surface',
    sm: 'px-3 py-2 rounded-control',
  },
  ghost: {
    md: 'p-0',
    sm: 'p-0',
  },
};

const slotPadding: Record<InputSize, { left: string; right: string }> = {
  md: { left: 'pl-10', right: 'pr-10' },
  sm: { left: 'pl-9', right: 'pr-9' },
};

const variantBase: Record<InputVariant, string> = {
  default:
    'w-full border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-neutral-800',
  ghost:
    'w-full border-0 bg-transparent focus:outline-none focus:ring-0 text-neutral-800 placeholder:text-neutral-300',
};
const errorStyles = 'border-error-border focus:ring-error-fg';
const disabledStyles = 'bg-neutral-50 text-neutral-500 cursor-not-allowed';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(props, ref) {
    const {
      size = 'md',
      variant = 'default',
      error = false,
      label,
      helpText,
      wrapperClassName,
      leftSlot,
      rightSlot,
      labelEnd,
      className = '',
      id: providedId,
      disabled,
      required,
      ...rest
    } = props;

    const generatedId = useId();
    const id = providedId || generatedId;
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const errorMessage = typeof error === 'string' ? error : undefined;
    const hasError = Boolean(error);
    const describedBy = [
      errorMessage ? errorId : null,
      helpText && !errorMessage ? helpId : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

    const inputClassName = cn(
      variantBase[variant],
      sizeStyles[variant][size],
      variant === 'default' && leftSlot && slotPadding[size].left,
      variant === 'default' && rightSlot && slotPadding[size].right,
      hasError && variant === 'default' && errorStyles,
      disabled && variant === 'default' && disabledStyles,
      className,
    );

    const inputEl = (
      <input
        ref={ref}
        id={id}
        className={inputClassName}
        disabled={disabled}
        required={required}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        {...rest}
      />
    );

    return (
      <div className={wrapperClassName || undefined}>
        {labelEnd ? (
          <div className="flex items-end justify-between gap-2 mb-2">
            {label ? (
              <label htmlFor={id} className="block text-sm font-medium text-neutral-600">
                {label}
                {required && <span className="text-error-fg ml-0.5" aria-hidden="true">*</span>}
              </label>
            ) : (
              <span aria-hidden="true" />
            )}
            {labelEnd}
          </div>
        ) : (
          label && (
            <label htmlFor={id} className="block text-sm font-medium text-neutral-600 mb-2">
              {label}
              {required && <span className="text-error-fg ml-0.5" aria-hidden="true">*</span>}
            </label>
          )
        )}
        {leftSlot || rightSlot ? (
          <div className="relative">
            {leftSlot && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-neutral-500">
                {leftSlot}
              </div>
            )}
            {inputEl}
            {rightSlot && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-neutral-500">
                {rightSlot}
              </div>
            )}
          </div>
        ) : (
          inputEl
        )}
        {helpText && !errorMessage && (
          <p id={helpId} className="text-xs text-neutral-500 mt-1">{helpText}</p>
        )}
        {errorMessage && (
          <p id={errorId} className="text-xs text-error-fg mt-1" role="alert">{errorMessage}</p>
        )}
      </div>
    );
  }
);
